import Avatar from '@sdk/components/avatar/Avatar';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import AttachmentsPreview from '@sdk/attachments/components/AttachmentsPreview/AttachmentsPreview';
import gettext from '@sdk/lang/gettext';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import { useTheme } from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';

import { ArticleComment as Comment } from '@web/newsfeed/types/Article';
import { UserNameLink } from '@web/components/UserNameLink/UserNameLink';
import { MarkdownDisplayWrapper } from '@web/components/MarkdownDisplayWrapper';
import {
  ContentContainer,
  FooterText,
  IconWithCounterWrapper,
  LikesAndCommentsWrapper,
} from '@web/newsfeed/Article/article.styles';
import {
  ActionsContainer,
  Wrapper,
} from '@web/newsfeed/Article/ArticleComments/ArticleComment/articleComment.styles';
import { RelativeDateString } from '@web/components/RelativeDateString/RelativeDateString';
import LikeGraySvg from '@web/assets/svg/Icons/16/thumbup-gray.svg';
import LikeBlueSvg from '@web/assets/svg/Icons/16/thumbup-blue.svg';
import { CommentActions } from '@web/newsfeed/Article/CommentActions/CommentActions';
import { useCommentActions } from '@web/newsfeed/Article/ArticleComments/hooks/useCommentActions';
import { useEditCommentModalContext } from '@web/newsfeed/ArticleSearch/EditCommentModalProvider/EditCommentModalProvider';
import { useParsedContent } from '@web/common/hooks/useParsedContent';

type Props = {
  comment: Comment;
  onLikeCounterClick: () => void;
};

export const ArticleComment = ({ comment, onLikeCounterClick }: Props) => {
  const theme = useTheme();
  const { setEditComment } = useEditCommentModalContext();
  const { attachments, content, likesCount, likedByMe, posted, user } = comment;
  const parsedContent = useParsedContent(content, null);
  const hasLikes = Boolean(likesCount);
  const { toggleCommentLike } = useCommentActions();

  if (comment.isDeleted) {
    return null;
  }

  return (
    <Wrapper>
      <Avatar user={user} />
      <ContentContainer>
        <Row>
          <UserNameLink lineHeight={22} user={user} themeColor={ThemeColorType.primary} />
          <ActionsContainer>
            <CommentActions comment={comment} onEdit={() => setEditComment(comment)} />
          </ActionsContainer>
        </Row>
        <MarkdownDisplayWrapper dangerouslySetInnerHTML={parsedContent} />
        {!!attachments?.length && <AttachmentsPreview attachments={attachments} />}
        <LikesAndCommentsWrapper mTop={5}>
          <FooterText clickable highlighted={likedByMe} onClick={() => toggleCommentLike(comment)}>
            {gettext('Like')}
          </FooterText>

          <FooterText>•</FooterText>
          <RelativeDateString date={posted} fontSize={12} themeColor={ThemeColorType.iconGray} />

          {hasLikes && <FooterText>•</FooterText>}
          {hasLikes && (
            <FooterText highlighted={likedByMe} clickable>
              <IconWithCounterWrapper
                onClick={(e) => {
                  e.stopPropagation();
                  onLikeCounterClick();
                }}>
                <StyledSvg
                  svg={likedByMe ? LikeBlueSvg : LikeGraySvg}
                  color={likedByMe ? theme.colors.primary : 'inherit'}></StyledSvg>
                <span>{likesCount}</span>
              </IconWithCounterWrapper>
            </FooterText>
          )}
        </LikesAndCommentsWrapper>
      </ContentContainer>
    </Wrapper>
  );
};
